import { useQuery } from '@apollo/client';
import {
    faCalendarDays,
    faFileLines,
    faLayerGroup,
    faServer,
    faTriangleExclamation,
    faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { camelCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Button from '../../../core/components/Button.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import SelectField from '../../../core/components/Fields/SelectField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import TagsField from '../../../core/components/Fields/TagsField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Switch from '../../../core/components/Switch.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { LookupDefinitionSelectField } from '../../../document/components/MasterDataConfig.tsx';
import {
    ERP_TOOL_PROVIDER_IMAGES,
    ERP_TOOL_PROVIDER_QUERIES,
    ERP_TOOL_PROVIDER_QUERY_KEYS,
    erpToolProviderTranslationKey,
    isErpToolProvider,
    NO_INTEGRATIONS_QUERY,
} from '../../assistants/components/config/erpToolProviders.ts';
import { GET_AVAILABLE_TOOL_PROVIDERS } from '../../assistants/queries.ts';
import { DANGEROUS_TOOLS, ERP_DEPENDENT_TOOLS, TEAMS_DEPENDENT_TOOLS } from '../constants.ts';
import { GET_TEAMS_INTEGRATION, GET_VOICE_AGENT_AVAILABLE_DOCUMENT_FIELDS } from '../queries.ts';
import { invalidEmailDomains } from '../utils/recipients.ts';

// Tools grouped by the system they connect to. The categories are the source of truth for the tool list and
// order in the config UI; every configurable tool belongs to exactly one category.
const TOOL_CATEGORIES = [
    {
        key: 'masterData',
        icon: withIcon(faLayerGroup),
        tools: ['article_search', 'order_history', 'order_lookup', 'open_offers', 'delivery_address', 'client_search'],
    },
    {
        key: 'contacts',
        icon: withIcon(faUserGroup),
        tools: [
            'internal_contact_search',
            'forward_to_internal_number',
            'get_teams_status',
            'send_teams_message',
            'send_email',
        ],
    },
    { key: 'meetings', icon: withIcon(faCalendarDays), tools: ['meeting_availability', 'book_meeting'] },
    { key: 'documents', icon: withIcon(faFileLines), tools: ['create_order', 'create_rfq'] },
    { key: 'erp', icon: withIcon(faServer), tools: ['article_price', 'article_availability'] },
] as const;

interface FieldOption {
    key: string;
    label: string;
}

const FieldChecklist = ({
    title,
    options,
    selected,
    onChange,
}: {
    title: string;
    options: FieldOption[];
    selected: string[];
    onChange: (next: string[]) => void;
}) => {
    const { t } = useTranslation('voice_agents');
    const allKeys = options.map((option) => option.key);
    const allSelected = allKeys.length > 0 && allKeys.every((key) => selected.includes(key));

    return (
        <div className="flex flex-col gap-0.5">
            <div className="flex items-center justify-between px-1.5 pb-0.5">
                <span className="text-xs font-medium text-content-medium">{title}</span>
                <Button
                    type="button"
                    variant="ghost"
                    className="text-content-brand-default py-0.5 text-xs"
                    onClick={() => onChange(allSelected ? [] : allKeys)}
                >
                    {allSelected ? t('fields.deselectAll') : t('fields.selectAll')}
                </Button>
            </div>
            {options.map((option) => (
                <label
                    key={option.key}
                    className="flex gap-2 items-center hover:bg-surface-medium p-1.5 rounded cursor-pointer"
                >
                    <Checkbox
                        checked={selected.includes(option.key)}
                        onChange={(e) =>
                            onChange(
                                e.target.checked
                                    ? [...selected, option.key]
                                    : selected.filter((key) => key !== option.key)
                            )
                        }
                    />
                    <span className="text-sm">{option.label}</span>
                </label>
            ))}
        </div>
    );
};

const DocumentFieldsCard = ({
    title,
    catalog,
    value,
    onChange,
}: {
    title: string;
    catalog?: { headerFields: FieldOption[]; lineItemFields: FieldOption[] };
    value: { headerFields: string[]; lineItemFields: string[] } | null;
    onChange: (next: { headerFields: string[]; lineItemFields: string[] }) => void;
}) => {
    const { t } = useTranslation('voice_agents');
    if (!catalog) return null;
    const selection = value || { headerFields: [], lineItemFields: [] };

    return (
        <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-content-strong">{title}</span>
            <div className="flex flex-col gap-3 max-h-80 overflow-y-auto border border-solid border-line-weak rounded p-2">
                <FieldChecklist
                    title={t('fields.headerFields')}
                    options={catalog.headerFields}
                    selected={selection.headerFields}
                    onChange={(headerFields) => onChange({ ...selection, headerFields })}
                />
                <FieldChecklist
                    title={t('fields.lineItemFields')}
                    options={catalog.lineItemFields}
                    selected={selection.lineItemFields}
                    onChange={(lineItemFields) => onChange({ ...selection, lineItemFields })}
                />
            </div>
        </div>
    );
};

const WarningIcon = withIcon(faTriangleExclamation);

const ToolSwitchCard = ({
    toolName,
    isChecked,
    onIsCheckedChange,
    disabled = false,
    isDangerous = false,
    children,
}: {
    toolName: string;
    isChecked: boolean;
    onIsCheckedChange: (checked: boolean) => void;
    disabled?: boolean;
    isDangerous?: boolean;
    children?: React.ReactNode;
}) => {
    const { t } = useTranslation('voice_agents');
    const key = camelCase(toolName);

    return (
        <div
            className={classnames(
                'rounded-xl flex flex-col gap-2 p-2 border border-solid bg-surface-weak shadow shadow-black/5 transition-colors',
                isDangerous ? 'border-line-danger-default' : 'border-line-weak',
                disabled && 'opacity-50'
            )}
        >
            <label
                className={classnames(
                    'flex flex-col group transition-colors rounded-md',
                    disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:border-line-strong hover:bg-surface-medium'
                )}
            >
                <div className="flex justify-between items-center gap-2 p-2 pb-0">
                    <div className="flex items-center gap-2 min-w-0">
                        <h3 className="font-semibold text-content-strong">{t(`tools.${key}.name`)}</h3>
                        {isDangerous && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-content-danger-strong bg-surface-danger-weak rounded-full px-2 py-0.5 whitespace-nowrap">
                                <WarningIcon />
                                {t('dangerousTool.badge')}
                            </span>
                        )}
                    </div>
                    <Switch value={isChecked} onValueChange={onIsCheckedChange} disabled={disabled} />
                </div>
                <div className="text-sm text-content-medium px-2 pb-2">{t(`tools.${key}.description`)}</div>
                {isDangerous && (
                    <div className="text-sm text-content-danger-strong px-2 pb-2">
                        {t(`tools.${key}.dangerWarning`)}
                    </div>
                )}
            </label>
            {children && <div className="px-2 pb-2">{children}</div>}
        </div>
    );
};

const SendEmailFallbackField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <Controller
            name="fallbackEmail"
            control={form.control}
            rules={{
                pattern: {
                    value: /^\S+@\S+\.\S+$/,
                    message: t('form.fallbackEmailInvalidError') as string,
                },
            }}
            render={({ field }) => (
                <FormField
                    label={t('form.fallbackEmail')}
                    error={form.formState.errors.fallbackEmail}
                    helpText={t('form.fallbackEmailHelpText')}
                    className={classnames(!isToolEnabled && 'opacity-50')}
                >
                    <StringField {...field} placeholder="office@company.com" disabled={!isToolEnabled} />
                </FormField>
            )}
        />
    );
};

const SendEmailAllowedDomainsField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <Controller
            name="sendEmailAllowedDomains"
            control={form.control}
            rules={{
                validate: (value) => {
                    const invalid = invalidEmailDomains(value);
                    return (
                        !invalid.length ||
                        (t('form.sendEmailAllowedDomainsInvalidError', { domains: invalid.join(', ') }) as string)
                    );
                },
            }}
            render={({ field }) => (
                <FormField
                    label={t('form.sendEmailAllowedDomains')}
                    error={form.formState.errors.sendEmailAllowedDomains}
                    helpText={t('form.sendEmailAllowedDomainsHelpText')}
                    className={classnames(!isToolEnabled && 'opacity-50')}
                >
                    <TagsField
                        {...field}
                        onValueChange={field.onChange}
                        placeholder="company.com"
                        disabled={!isToolEnabled}
                    />
                </FormField>
            )}
        />
    );
};

const CalendlyMeetingLinkField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <div className="flex flex-col gap-3">
            <Controller
                name="calendlyMeetingLink"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.calendlyMeetingLink')}
                        error={form.formState.errors.calendlyMeetingLink}
                        helpText={t('form.calendlyMeetingLinkHelpText')}
                        className={classnames(!isToolEnabled && 'opacity-50')}
                    >
                        <StringField
                            {...field}
                            placeholder="https://calendly.com/your-team/workist-demo"
                            disabled={!isToolEnabled}
                        />
                    </FormField>
                )}
            />
            <Controller
                name="calendlyApiToken"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.calendlyApiToken')}
                        error={form.formState.errors.calendlyApiToken}
                        helpText={t(
                            form.watch('calendlyApiTokenSet')
                                ? 'form.calendlyApiTokenSetHelpText'
                                : 'form.calendlyApiTokenHelpText'
                        )}
                        className={classnames(!isToolEnabled && 'opacity-50')}
                    >
                        <StringField
                            {...field}
                            type="password"
                            autoComplete="off"
                            placeholder={form.watch('calendlyApiTokenSet') ? '••••••••••••' : 'eyJra...'}
                            disabled={!isToolEnabled}
                        />
                    </FormField>
                )}
            />
            <Controller
                name="calendlySummaryQuestion"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('form.calendlySummaryQuestion')}
                        error={form.formState.errors.calendlySummaryQuestion}
                        helpText={t('form.calendlySummaryQuestionHelpText')}
                        className={classnames(!isToolEnabled && 'opacity-50')}
                    >
                        <StringField {...field} placeholder="Call Zusammenfassung" disabled={!isToolEnabled} />
                    </FormField>
                )}
            />
        </div>
    );
};

const ArticleLookupField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <Controller
            name="articleLookupDefinitionId"
            control={form.control}
            render={({ field }) => (
                <FormField
                    label={t('form.articleLookupDefinitionId')}
                    error={form.formState.errors.articleLookupDefinitionId}
                    helpText={t('form.articleLookupDefinitionIdHelpText')}
                    className={classnames(!isToolEnabled && 'opacity-50')}
                >
                    <LookupDefinitionSelectField field={field} lookupType="ARTICLE_LOOKUP" disabled={!isToolEnabled} />
                </FormField>
            )}
        />
    );
};

const OrderHistoryLookupField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <Controller
            name="orderHistoryLookupDefinitionId"
            control={form.control}
            render={({ field }) => (
                <FormField
                    label={t('form.orderHistoryLookupDefinitionId')}
                    error={form.formState.errors.orderHistoryLookupDefinitionId}
                    helpText={t('form.orderHistoryLookupDefinitionIdHelpText')}
                    className={classnames(!isToolEnabled && 'opacity-50')}
                >
                    <LookupDefinitionSelectField
                        field={field}
                        lookupType="ORDER_MATCHING_LOOKUP"
                        disabled={!isToolEnabled}
                    />
                </FormField>
            )}
        />
    );
};

// The order_lookup tool reads its OWN order matching lookup, not the order history one: an unidentified caller
// may be served from a reduced lookup that holds less than the one identified callers get.
const OrderLookupDefinitionField = ({ form, isToolEnabled }: { form: any; isToolEnabled: boolean }) => {
    const { t } = useTranslation('voice_agents');

    return (
        <Controller
            name="orderLookupDefinitionId"
            control={form.control}
            render={({ field }) => (
                <FormField
                    label={t('form.orderLookupDefinitionId')}
                    error={form.formState.errors.orderLookupDefinitionId}
                    helpText={t('form.orderLookupDefinitionIdHelpText')}
                    className={classnames(!isToolEnabled && 'opacity-50')}
                >
                    <LookupDefinitionSelectField
                        field={field}
                        lookupType="ORDER_MATCHING_LOOKUP"
                        disabled={!isToolEnabled}
                    />
                </FormField>
            )}
        />
    );
};

const providerOptionContent = (option: { label: string; logo?: string }) => (
    <span className="flex items-center gap-2">
        {option.logo && <img src={option.logo} alt="" className="w-auto h-4 shrink-0" />}
        <span className="text-content-strong">{option.label}</span>
    </span>
);

/**
 * Picks the ERP the article_price / article_availability tools ask: the system, and — when that system has more
 * than one configured connection — which connection. Every tool provider the customer can use is offered, so an
 * ERP that implements those tools shows up here without a change to this file.
 */
const ErpConnectionFields = ({ form }: { form: any }) => {
    const { t } = useTranslation('voice_agents');
    const { t: tAgents } = useTranslation('agents');

    const { data, loading } = useQuery(GET_AVAILABLE_TOOL_PROVIDERS);
    const providers = (data?.availableToolProviders || []).filter((provider: { key: string }) =>
        isErpToolProvider(provider.key)
    );

    const providerKey: string = form.watch('erpToolProviderKey') || '';
    const integrationsQuery = ERP_TOOL_PROVIDER_QUERIES[providerKey];
    const { data: integrationsData, loading: integrationsLoading } = useQuery(
        integrationsQuery ?? NO_INTEGRATIONS_QUERY,
        { skip: !integrationsQuery }
    );
    const integrations = integrationsQuery ? (integrationsData?.[ERP_TOOL_PROVIDER_QUERY_KEYS[providerKey]] ?? []) : [];
    // Only an agent whose company has no ERP at all has nothing to pick here; otherwise this is the one active
    // control on the panel, since the tools below it stay locked until it is set.
    const hasErpAvailable = loading || providers.length > 0;

    // Removing the connection leaves the tools with nothing to ask, so they go off with it. This is the user's
    // own action on this field, not a silent correction of their selection.
    const disableErpTools = () => {
        const enabled: string[] = form.getValues('enabledTools') || [];
        const remaining = enabled.filter((name: string) => !ERP_DEPENDENT_TOOLS.includes(name));
        if (remaining.length !== enabled.length) {
            form.setValue('enabledTools', remaining, { shouldDirty: true });
        }
    };

    const providerOptions = providers.map((provider: { key: string; name: string }) => ({
        value: provider.key,
        label: tAgents(`toolProviders.${erpToolProviderTranslationKey(provider.key)}.name`, provider.name),
        logo: ERP_TOOL_PROVIDER_IMAGES[provider.key],
    }));
    const integrationOptions = integrations.map((integration: { id: string; name: string }) => ({
        value: integration.id,
        label: integration.name,
    }));

    return (
        <div
            className={classnames(
                'rounded-xl flex flex-col gap-2 p-2 border border-line-weak border-solid bg-surface-weak shadow shadow-black/5',
                !hasErpAvailable && 'opacity-50'
            )}
        >
            {/* Same anatomy as a tool card — a strong heading over a muted description — so the one control that
                is always live is not read as yet another disabled card. */}
            <div className="flex flex-col p-2 pb-0">
                <h3 className="font-semibold text-content-strong">{t('erpConnection.title')}</h3>
                <div className="text-sm text-content-medium">{t('erpConnection.description')}</div>
            </div>

            <div className="flex flex-col gap-3 px-2 pb-2">
                <Controller
                    name="erpToolProviderKey"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('erpConnection.systemLabel')}
                            helpText={hasErpAvailable ? undefined : t('erpConnection.noIntegrationsHint')}
                        >
                            <SelectField
                                options={providerOptions}
                                placeholder={t('erpConnection.systemPlaceholder')}
                                value={field.value || undefined}
                                isLoading={loading}
                                disabled={!hasErpAvailable}
                                onValueChange={(value) => {
                                    field.onChange(value || '');
                                    // the connection belongs to the system that was picked — never carry it over
                                    form.setValue('erpToolProviderIntegrationId', '', { shouldDirty: true });
                                    // ... which leaves the tools unconnected, unless the new system needs none
                                    if (!value || ERP_TOOL_PROVIDER_QUERIES[value]) disableErpTools();
                                }}
                                renderOption={providerOptionContent}
                                renderValue={(value, option) => (option ? providerOptionContent(option as any) : value)}
                                modal
                            />
                        </FormField>
                    )}
                />

                {providerKey && integrationOptions.length > 0 && (
                    <Controller
                        name="erpToolProviderIntegrationId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('erpConnection.integrationLabel')}
                                helpText={t('erpConnection.integrationHelpText')}
                            >
                                <SelectField
                                    options={integrationOptions}
                                    placeholder={t('erpConnection.integrationPlaceholder')}
                                    value={field.value || undefined}
                                    isLoading={integrationsLoading}
                                    onValueChange={(value) => {
                                        field.onChange(value || '');
                                        if (!value) disableErpTools();
                                    }}
                                    modal
                                />
                            </FormField>
                        )}
                    />
                )}
            </div>
        </div>
    );
};

const DefaultChannelsCard = ({
    value,
    onChange,
    requireOrder,
    requireRfq,
}: {
    value: { orderChannelId: string; rfqChannelId: string } | null;
    onChange: (config: { orderChannelId: string; rfqChannelId: string }) => void;
    requireOrder: boolean;
    requireRfq: boolean;
}) => {
    const { t } = useTranslation('voice_agents');
    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS);

    const toOptions = (channels?: Array<{ id: string; name: string }>) =>
        (channels || []).map((channel) => ({ value: channel.id, label: channel.name }));
    const orderChannelOptions = toOptions(channelData?.orderProcessingChannels);
    const rfqChannelOptions = toOptions(channelData?.rfqProcessingChannels);

    const config = value || { orderChannelId: '', rfqChannelId: '' };

    return (
        <div className="flex flex-col gap-4 bg-surface-medium rounded-md p-3">
            <div className="flex flex-col">
                <span className="text-sm font-medium text-content-strong">
                    {t('toolProviders.defaultChannels.name')}
                </span>
                <span className="text-xs text-content-medium">{t('toolProviders.defaultChannels.description')}</span>
            </div>
            <FormField label={t('toolProviders.defaultChannels.orderChannelLabel')} required={requireOrder}>
                <SelectField
                    options={orderChannelOptions}
                    placeholder={t('toolProviders.defaultChannels.orderChannelPlaceholder')}
                    value={config.orderChannelId || ''}
                    onValueChange={(orderChannelId) => onChange({ ...config, orderChannelId })}
                    modal
                />
            </FormField>
            <FormField label={t('toolProviders.defaultChannels.rfqChannelLabel')} required={requireRfq}>
                <SelectField
                    options={rfqChannelOptions}
                    placeholder={t('toolProviders.defaultChannels.rfqChannelPlaceholder')}
                    value={config.rfqChannelId || ''}
                    onValueChange={(rfqChannelId) => onChange({ ...config, rfqChannelId })}
                    modal
                />
            </FormField>
        </div>
    );
};

const VoiceAgentToolsFormFields = ({ form }) => {
    const { t } = useTranslation('voice_agents');

    const { data: fieldData } = useQuery(GET_VOICE_AGENT_AVAILABLE_DOCUMENT_FIELDS);
    // errors (e.g. missing manager permissions) are treated as "no Teams account connected"
    const { data: teamsData } = useQuery(GET_TEAMS_INTEGRATION, { errorPolicy: 'ignore' });
    const catalog = fieldData?.voiceAgentAvailableDocumentFields;
    const isTeamsConnected = Boolean(teamsData?.teamsIntegration?.isConnected);
    const enabledTools: string[] = form.watch('enabledTools') || [];

    // On create, default a document type's field selection to "all available" once its create tool is enabled.
    useEffect(() => {
        if (!catalog) return;
        const current = form.getValues('documentFields') || { order: null, rfq: null };
        const next = { ...current };
        let changed = false;
        if (enabledTools.includes('create_order') && !current.order) {
            next.order = {
                headerFields: catalog.order.headerFields.map((f: FieldOption) => f.key),
                lineItemFields: catalog.order.lineItemFields.map((f: FieldOption) => f.key),
            };
            changed = true;
        }
        if (enabledTools.includes('create_rfq') && !current.rfq) {
            next.rfq = {
                headerFields: catalog.rfq.headerFields.map((f: FieldOption) => f.key),
                lineItemFields: catalog.rfq.lineItemFields.map((f: FieldOption) => f.key),
            };
            changed = true;
        }
        if (changed) form.setValue('documentFields', next);
    }, [catalog, enabledTools.includes('create_order'), enabledTools.includes('create_rfq')]);

    const [activeCategoryKey, setActiveCategoryKey] = useState<string>(TOOL_CATEGORIES[0].key);
    // a dangerous tool the user is switching on, waiting for them to confirm the risk
    const [pendingDangerousTool, setPendingDangerousTool] = useState<string | null>(null);

    return (
        <Controller
            name="enabledTools"
            control={form.control}
            render={({ field }) => {
                const enabled: string[] = field.value || [];
                const toggle = (toolName: string, checked: boolean) =>
                    field.onChange(checked ? [...enabled, toolName] : enabled.filter((name) => name !== toolName));
                const requestToggle = (toolName: string, checked: boolean) => {
                    if (checked && DANGEROUS_TOOLS.includes(toolName)) {
                        setPendingDangerousTool(toolName);
                        return;
                    }
                    toggle(toolName, checked);
                };

                const activeCategory =
                    TOOL_CATEGORIES.find((category) => category.key === activeCategoryKey) ?? TOOL_CATEGORIES[0];
                const ActiveIcon = activeCategory.icon;
                const activeCount = activeCategory.tools.filter((name) => enabled.includes(name)).length;
                const needsChannels = enabled.includes('create_order') || enabled.includes('create_rfq');
                // A system whose connections are configured per tenant is only usable once one of them is
                // picked; a provider without any (the demo ERP) is ready as soon as it is selected.
                const erpProviderKey: string = form.watch('erpToolProviderKey') || '';
                const isErpConnected =
                    Boolean(erpProviderKey) &&
                    (!ERP_TOOL_PROVIDER_QUERIES[erpProviderKey] || Boolean(form.watch('erpToolProviderIntegrationId')));

                return (
                    <>
                        <div className="flex flex-col md:flex-row border border-solid border-line-weak rounded-xl overflow-hidden bg-surface-weak min-h-[24rem]">
                            <nav className="flex md:flex-col gap-1 p-2 shrink-0 overflow-x-auto border-b md:border-b-0 md:border-r border-solid border-line-weak md:w-60">
                                {TOOL_CATEGORIES.map((category) => {
                                    const CategoryIcon = category.icon;
                                    const count = category.tools.filter((name) => enabled.includes(name)).length;
                                    const isActive = category.key === activeCategoryKey;
                                    return (
                                        <button
                                            key={category.key}
                                            type="button"
                                            onClick={() => setActiveCategoryKey(category.key)}
                                            className={classnames(
                                                'flex items-center gap-3 p-2 rounded-lg text-left shrink-0 md:w-full transition-colors',
                                                isActive ? 'bg-surface-medium' : 'hover:bg-surface-medium'
                                            )}
                                        >
                                            <span
                                                className={classnames(
                                                    'w-8 h-8 rounded-md flex items-center justify-center shrink-0 border border-solid border-line-weak bg-surface-weak',
                                                    isActive ? 'text-content-brand-default' : 'text-content-medium'
                                                )}
                                            >
                                                <CategoryIcon />
                                            </span>
                                            <span className="flex flex-col min-w-0">
                                                <span className="text-sm font-medium text-content-strong whitespace-nowrap">
                                                    {t(`categories.${category.key}.name`)}
                                                </span>
                                                <span className="text-xs text-content-weak tabular-nums whitespace-nowrap">
                                                    {t('categories.enabledCount', {
                                                        enabled: count,
                                                        total: category.tools.length,
                                                    })}
                                                </span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </nav>

                            <div className="flex-1 min-w-0 p-4 flex flex-col gap-3">
                                <div className="flex items-start gap-3 pb-3 border-b border-solid border-line-weak">
                                    <span className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border border-solid border-line-weak bg-surface-weak text-content-brand-default">
                                        <ActiveIcon />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-sm font-semibold text-content-strong">
                                            {t(`categories.${activeCategory.key}.name`)}
                                        </h3>
                                        <p className="text-xs text-content-medium">
                                            {t(`categories.${activeCategory.key}.description`)}
                                        </p>
                                    </div>
                                    <span className="text-xs font-medium text-content-medium bg-surface-medium rounded-full px-2.5 py-1 tabular-nums whitespace-nowrap">
                                        {t('categories.enabledCount', {
                                            enabled: activeCount,
                                            total: activeCategory.tools.length,
                                        })}
                                    </span>
                                </div>

                                {activeCategory.key === 'erp' && <ErpConnectionFields form={form} />}

                                <div className="flex flex-col gap-2">
                                    {activeCategory.tools.map((toolName) => {
                                        // Tools that depend on a connection cannot be enabled before it exists;
                                        // already-enabled ones stay editable so they can still be switched off.
                                        const isMissingConnection =
                                            (!isTeamsConnected && TEAMS_DEPENDENT_TOOLS.includes(toolName)) ||
                                            (!isErpConnected && ERP_DEPENDENT_TOOLS.includes(toolName));
                                        const isLocked = isMissingConnection && !enabled.includes(toolName);
                                        return (
                                            <ToolSwitchCard
                                                key={toolName}
                                                toolName={toolName}
                                                isChecked={enabled.includes(toolName)}
                                                onIsCheckedChange={(checked) => requestToggle(toolName, checked)}
                                                disabled={isLocked}
                                                isDangerous={DANGEROUS_TOOLS.includes(toolName)}
                                            >
                                                {toolName === 'send_email' && (
                                                    <div className="flex flex-col gap-3">
                                                        <SendEmailFallbackField
                                                            form={form}
                                                            isToolEnabled={enabled.includes('send_email')}
                                                        />
                                                        <SendEmailAllowedDomainsField
                                                            form={form}
                                                            isToolEnabled={enabled.includes('send_email')}
                                                        />
                                                    </div>
                                                )}
                                                {toolName === 'article_search' && (
                                                    <ArticleLookupField
                                                        form={form}
                                                        isToolEnabled={enabled.includes('article_search')}
                                                    />
                                                )}
                                                {toolName === 'order_history' && (
                                                    <OrderHistoryLookupField
                                                        form={form}
                                                        isToolEnabled={enabled.includes('order_history')}
                                                    />
                                                )}
                                                {toolName === 'order_lookup' && (
                                                    <OrderLookupDefinitionField
                                                        form={form}
                                                        isToolEnabled={enabled.includes('order_lookup')}
                                                    />
                                                )}
                                                {toolName === 'meeting_availability' && (
                                                    <CalendlyMeetingLinkField
                                                        form={form}
                                                        isToolEnabled={
                                                            enabled.includes('meeting_availability') ||
                                                            enabled.includes('book_meeting')
                                                        }
                                                    />
                                                )}
                                            </ToolSwitchCard>
                                        );
                                    })}
                                </div>

                                {activeCategory.key === 'contacts' && !isTeamsConnected && (
                                    <p className="text-xs text-content-weak">{t('teamsIntegration.configureHint')}</p>
                                )}

                                {activeCategory.key === 'erp' && !isErpConnected && (
                                    <p className="text-xs text-content-weak">
                                        {t(
                                            erpProviderKey
                                                ? 'erpConnection.selectConnectionHint'
                                                : 'erpConnection.selectSystemHint'
                                        )}
                                    </p>
                                )}

                                {activeCategory.key === 'documents' && needsChannels && (
                                    <div className="flex flex-col gap-3">
                                        <Controller
                                            name="defaultChannels"
                                            control={form.control}
                                            render={({ field: channelsField }) => (
                                                <DefaultChannelsCard
                                                    value={channelsField.value}
                                                    onChange={channelsField.onChange}
                                                    requireOrder={enabled.includes('create_order')}
                                                    requireRfq={enabled.includes('create_rfq')}
                                                />
                                            )}
                                        />
                                        <p className="text-xs text-content-weak">{t('toolProviders.channelsHint')}</p>
                                        <Controller
                                            name="documentFields"
                                            control={form.control}
                                            render={({ field: fieldsField }) => {
                                                const value = fieldsField.value || { order: null, rfq: null };
                                                return (
                                                    <div className="flex flex-col gap-3">
                                                        <span className="text-sm text-content-medium">
                                                            {t('fields.intro')}
                                                        </span>
                                                        {enabled.includes('create_order') && (
                                                            <DocumentFieldsCard
                                                                title={t('fields.orderTitle')}
                                                                catalog={catalog?.order}
                                                                value={value.order}
                                                                onChange={(order) =>
                                                                    fieldsField.onChange({ ...value, order })
                                                                }
                                                            />
                                                        )}
                                                        {enabled.includes('create_rfq') && (
                                                            <DocumentFieldsCard
                                                                title={t('fields.rfqTitle')}
                                                                catalog={catalog?.rfq}
                                                                value={value.rfq}
                                                                onChange={(rfq) =>
                                                                    fieldsField.onChange({ ...value, rfq })
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <ConfirmModal
                            visible={pendingDangerousTool !== null}
                            title={t('dangerousTool.confirm.title', {
                                tool: t(`tools.${camelCase(pendingDangerousTool ?? '')}.name`),
                            })}
                            onConfirm={() => {
                                if (pendingDangerousTool) toggle(pendingDangerousTool, true);
                                setPendingDangerousTool(null);
                            }}
                            onCancel={() => setPendingDangerousTool(null)}
                            labelConfirm={t('dangerousTool.confirm.enable')}
                            labelCancel={t('dangerousTool.confirm.cancel')}
                            danger
                        >
                            <div className="flex flex-col gap-2 text-sm text-content-strong">
                                <p>{t(`tools.${camelCase(pendingDangerousTool ?? '')}.dangerWarning`)}</p>
                                <p>{t('dangerousTool.confirm.body')}</p>
                            </div>
                        </ConfirmModal>
                    </>
                );
            }}
        />
    );
};

export default VoiceAgentToolsFormFields;
